import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PAGE_SIZES } from "@/lib/constants";
import { Search, Star, MessageCircle, MapPin, Phone, ShieldCheck, ExternalLink, Camera, Map as MapIcon, LayoutGrid } from "lucide-react";
import ImageSearchModal from "@/components/ImageSearchModal";
import VendorMap from "@/components/VendorMap";
import SearchAutocomplete from "@/components/SearchAutocomplete";
import InlineAdBanner from "@/components/InlineAdBanner";
import { useLang } from "@/context/LangContext";

const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 400'>
       <defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
         <stop offset='0' stop-color='#f97316'/><stop offset='0.6' stop-color='#eab308'/><stop offset='1' stop-color='#16a34a'/>
       </linearGradient></defs>
       <rect width='100%' height='100%' fill='url(#g)'/>
       <text x='50%' y='52%' text-anchor='middle' font-family='Georgia' font-size='28' fill='white' opacity='0.95'>sanskariBazarIn</text>
     </svg>`
  );

const onImgError = (e) => {
  if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG;
};

export default function VendorListing() {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);

  const city = params.get("city") || "";
  const category = params.get("category") || "";
  const categoriesCsv = params.get("categories") || "";
  const search = params.get("search") || "";
  const [searchDraft, setSearchDraft] = useState(search);
  useEffect(() => { setSearchDraft(search); }, [search]);
  const verified = params.get("verified") === "1";
  const openNow = params.get("open_now") === "1";
  const minRating = params.get("min_rating") || "";
  const sort = params.get("sort") || "recent";
  const page = parseInt(params.get("page") || "1", 10);
  const pageSize = parseInt(params.get("page_size") || "50", 10);
  const view = params.get("view") === "map" ? "map" : "grid";

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const update = (patch) => {
    const np = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined || v === false) np.delete(k);
      else np.set(k, String(v));
    });
    if (!("page" in patch)) np.set("page", "1");
    setParams(np);
  };

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (city) q.city = city;
    if (categoriesCsv) q.categories = categoriesCsv;
    else if (category) q.category = category;
    if (search) q.search = search;
    if (verified) q.verified = true;
    if (openNow) q.open_now = true;
    if (minRating) q.min_rating = minRating;
    q.sort = sort;
    q.page = page;
    q.page_size = pageSize;
    api
      .get("/vendors", { params: q })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  }, [city, category, categoriesCsv, search, verified, openNow, minRating, sort, page, pageSize]);

  const title = useMemo(() => {
    if (category && city) return t("vl.title_category_city").replace("{category}", category).replace("{city}", city);
    if (category) return category;
    if (city) return t("vl.title_city").replace("{city}", city);
    return t("vl.title_default");
  }, [category, city, t]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{t("vl.eyebrow")}</div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {loading ? t("vl.loading") : t("vl.results_count").replace("{n}", total.toLocaleString())}
          </p>
        </div>
        <ImageSearchModal city={city || ""} category={category || ""} />
      </div>

      {/* Filter bar */}
      <Card className="p-4 border-border/60 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t("vl.label_search")}</label>
            <div className="mt-1">
              <SearchAutocomplete
                value={searchDraft}
                onChange={setSearchDraft}
                onSubmit={(v) => update({ search: v })}
                placeholder={t("vl.search_placeholder")}
                size="h-9"
                testId="vendor-filter-search"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t("vl.label_city")}</label>
            <Select value={city || "all"} onValueChange={(v) => update({ city: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="vendor-filter-city"><SelectValue placeholder={t("vl.all_cities")} /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">{t("vl.all_cities")}</SelectItem>
                {cities.map((c) => (
                  <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t("vl.label_category")}</label>
            <Select value={category || "all"} onValueChange={(v) => update({ category: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="vendor-filter-category"><SelectValue placeholder={t("vl.all_categories")} /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">{t("vl.all_categories")}</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t("vl.label_sort")}</label>
            <Select value={sort} onValueChange={(v) => update({ sort: v })}>
              <SelectTrigger className="mt-1" data-testid="vendor-filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">{t("vl.sort_recent")}</SelectItem>
                <SelectItem value="rating">{t("vl.sort_rating")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={verified}
              onCheckedChange={(v) => update({ verified: v ? 1 : "" })}
              data-testid="vendor-filter-verified"
            />
            <span>{t("vl.verified")}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={openNow}
              onCheckedChange={(v) => update({ open_now: v ? 1 : "" })}
              data-testid="vendor-filter-open-now"
            />
            <span>{t("vl.open_now")}</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{t("vl.min_rating")}</span>
            <Select value={minRating || "any"} onValueChange={(v) => update({ min_rating: v === "any" ? "" : v })}>
              <SelectTrigger className="h-8 w-24" data-testid="vendor-filter-rating"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("vl.rating_any")}</SelectItem>
                <SelectItem value="3">3+</SelectItem>
                <SelectItem value="4">4+</SelectItem>
                <SelectItem value="4.5">4.5+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex rounded-full border border-border/60 p-0.5" data-testid="vendor-view-toggle">
              <Button
                size="sm"
                variant={view === "grid" ? "default" : "ghost"}
                className="rounded-full h-7 px-3"
                onClick={() => update({ view: "grid" })}
                data-testid="vendor-view-grid"
              >
                <LayoutGrid className="h-3.5 w-3.5 mr-1" /> {t("vl.view_grid")}
              </Button>
              <Button
                size="sm"
                variant={view === "map" ? "default" : "ghost"}
                className="rounded-full h-7 px-3"
                onClick={() => update({ view: "map" })}
                data-testid="vendor-view-map"
              >
                <MapIcon className="h-3.5 w-3.5 mr-1" /> {t("vl.view_map")}
              </Button>
            </div>
            <span className="text-muted-foreground text-sm">{t("vl.per_page")}</span>
            <Select value={String(pageSize)} onValueChange={(v) => update({ page_size: v })}>
              <SelectTrigger className="h-8 w-20" data-testid="vendor-filter-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Sponsored banner (top of results) */}
      <InlineAdBanner slot="search-top" city={city} category={category} className="mb-4" />

      {/* Results */}
      {view === "map" ? (
        <div data-testid="vendor-results-map">
          <VendorMap vendors={items} height={620} />
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 stagger" data-testid="vendor-results">
        {items.map((v, i) => (
          <React.Fragment key={v.vendor_id}>
            {i === 6 && (
              <div className="md:col-span-2 lg:col-span-3">
                <InlineAdBanner slot="search-mid" city={city} category={category} />
              </div>
            )}
            <Card className="overflow-hidden border-border/60 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`vendor-card-${v.vendor_id}`}>
            <Link to={`/vendors/${v.vendor_id}`} className="block" data-testid={`vendor-image-link-${v.vendor_id}`}>
              <div className="relative h-36 bg-muted">
                {v.banner && <img src={v.banner} alt={v.business_name} onError={onImgError} className="h-full w-full object-cover" />}
                {v.is_verified && (
                  <Badge className="absolute top-3 left-3 bg-primary text-primary-foreground gap-1">
                    <ShieldCheck className="h-3 w-3" /> {t("vl.verified")}
                  </Badge>
                )}
                <div className="absolute -bottom-7 left-4 h-14 w-14 rounded-2xl border-4 border-card overflow-hidden bg-card">
                  {v.logo && <img src={v.logo} alt="" onError={onImgError} className="h-full w-full object-cover" />}
                </div>
              </div>
            </Link>
            <div className="p-4 pt-9">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Link to={`/vendors/${v.vendor_id}`} className="font-heading text-lg leading-tight hover:text-primary transition-colors block">
                    {v.business_name}
                  </Link>
                  <div className="text-xs text-muted-foreground mt-0.5">{v.owner_name}</div>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  {v.rating?.toFixed(1)}
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                <Badge variant="secondary" className="font-normal">{v.category}</Badge>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {v.city}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{v.description}</p>
              {search && Array.isArray(v.products) && (() => {
                const lc = search.toLowerCase();
                const matches = v.products.filter((p) => (p.name || "").toLowerCase().includes(lc)).slice(0, 3);
                if (matches.length === 0) return null;
                return (
                  <div className="mt-3" data-testid={`vendor-matched-items-${v.vendor_id}`}>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-primary mb-1">{t("vl.matched_items")}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {matches.map((p) => (
                        <Badge key={p.name} variant="outline" className="text-xs font-normal">{p.name}</Badge>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`vendor-view-${v.vendor_id}`}>
                  <Link to={`/vendors/${v.vendor_id}`} target="_blank" rel="noreferrer">
                    {t("vl.btn_view")} <ExternalLink className="h-3 w-3 ml-1" />
                  </Link>
                </Button>
                {v.whatsapp && (
                  <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`vendor-whatsapp-${v.vendor_id}`}>
                    <a
                      href={`https://wa.me/${v.whatsapp.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("vl.btn_whatsapp")}
                    </a>
                  </Button>
                )}
                <Button asChild size="sm" className="rounded-full" data-testid={`vendor-chat-${v.vendor_id}`}>
                  <Link to={`/vendors/${v.vendor_id}?chat=1`}><MessageCircle className="h-3 w-3 mr-1" /> {t("vl.btn_chat")}</Link>
                </Button>
              </div>
            </div>
          </Card>
          </React.Fragment>
        ))}
      </div>
      )}

      {/* Pagination */}
      <div className="mt-8 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {t("vl.pagination").replace("{page}", String(page)).replace("{total}", String(totalPages))}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={page <= 1}
            onClick={() => update({ page: page - 1 })}
            data-testid="vendor-prev-page"
          >
            {t("vl.previous")}
          </Button>
          <Button
            disabled={page >= totalPages}
            onClick={() => update({ page: page + 1 })}
            data-testid="vendor-next-page"
          >
            {t("vl.next")}
          </Button>
        </div>
      </div>
    </div>
  );
}
